import parse from "autosuggest-highlight/parse";
import { isEmpty, isNull } from "lodash";
import { debounce } from "@mui/material";
import Alert, { AlertColor } from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
// import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import validator from "validator";
import { APIProvider } from "@vis.gl/react-google-maps";

// Imported actions
import {
  deleteNotificationConfiguration,
  getContacts,
  getNotificationTypes,
  getNotificationConfigurations,
  getFaultTypes,
  updateNotificationConfiguration,
  getNotificationConfigurationLocationTypes,
} from "../../../../actions/alert.js";
import {
  getDistanceUnitTypes,
  getLatLon,
  getUsaStates,
} from "../../../../actions/geo.js";

// Imported assets
import { CheckmarkIcon } from "../../../../assets/icons/svgs/Icons.jsx";
import CancelIcon from "../../../../assets/icons/close.svg";
import CaretDownBlack from "../../../../assets/icons/DownCarrot_BlackIcon.svg";
import CaretDownWhite from "../../../../assets/icons/DownCarrot_WhiteIcon.svg";
import CaretUpBlack from "../../../../assets/icons/UpCarrot_BlackIcon.svg";
import CaretUpWhite from "../../../../assets/icons/UpCarrot_WhiteIcon.svg";
import EditIcon from "../../../../assets/icons/Edit_WhiteIcon.svg";
import SaveIcon from "../../../../assets/icons/Save_White.svg";
import TrashIconRed from "../../../../assets/icons/Delete_Red.svg";
import TrashIconWhite from "../../../../assets/icons/Delete_White.png";

// Imported components
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import FilterControls from "./components/FilterControls.js";
import Info from "../../../../components/form/Info";
import Label from "../../../../components/form/Label";
import Map from "../components/Map.js";
import NavigationBar from "../../../../components/nav/NavigationBar.tsx";
import NewContactModal from "../components/NewContactModal";
import PrimaryButton from "../../../../components/ui/PrimaryButton.js";

// Imported contexts
import { useUserContext } from "../../../../contexts/User.tsx";

// Imported utils
import { deepCopy } from "../../../../utils/misc.js";
import { isLoggedIn } from "../../../../utils/user.js";

// Styled components
const AddNewRecipientButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  // bottom: clamp(16px, 2vw, 28px);
  bottom: 12px;
  cursor: pointer;
  font-size: clamp(12px, 1.5vw, 18px);
  height: clamp(24px, 3vw, 32px);
  // left: clamp(10px, 2vw, 20px);
  left: 14px;
  position: absolute;
  width: auto;
  z-index: 100;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const ControlsButton = styled(PrimaryButton)`
  display: flex;
  flex-direction: row;
  gap: clamp(6px, 1.5vw, 16px);
  justify-content: flex-start;
  width: clamp(100px, 8vw, 140px);
`;

const CancelButton = styled(ControlsButton)`
  background: var(--terbine-dark-gray);
  color: #ffffff;

  &:hover {
    background: lab(from var(--terbine-dark-gray) calc(l - 5) a b / 100);
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 3vw, 40px);
  justify-content: flex-start;
  padding: clamp(40px, 5vw, 80px) clamp(50px, 5vw, 100px) 0;
  position: relative;
`;

const ControlsSection = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vw, 10px);
  min-width: clamp(140px, 15vw, 200px);
`;

const CreateNewButton = styled(PrimaryButton)`
  align-self: flex-start;
  background: var(--terbine-blue);
  color: #ffffff;
  height: clamp(30px, 4vw, 40px);

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const CreateNewSection = styled.div`
  display: flex;
  flex-direction: row;
`;

const DeleteButton = styled(ControlsButton)`
  background: red;
  color: #ffffff;

  &:hover {
    background: lab(from red calc(l - 5) a b / 100);
  }
`;

const DeleteIcon = styled.img`
  flex-shrink: 0;
  width: clamp(18px, 2.5vw, 28px);
  height: clamp(18px, 2.5vw, 28px);
  position: relative;
  overflow: visible;
  cursor: pointer;
`;

const EditButton = styled(ControlsButton)`
  background: var(--terbine-blue);
  color: #ffffff;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const FaultsChip = styled.div`
  align-content: center;
  align-items: center;
  background: white;
  border: 1px solid black;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  padding: clamp(6px, 1.5vw, 10px) clamp(12px, 2.5vw, 20px);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: clamp(20px, 4vw, 40px);
  align-items: center;
  justify-content: center;
  align-self: stretch;
  position: relative;
  min-width: clamp(800px, 70vw, 1100px);
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vw, 8px);
  justify-content: flex-start;
  position: relative;

  & .MuiInputBase-root {
    background: #ffffff;
  }
`;

const Icon = styled.img`
  height: auto;
  margin-left: clamp(6px, 1.5vw, 10px);
  width: clamp(10px, 1.5vw, 16px);
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: clamp(12px, 2vw, 24px);
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  width: 100%;
  position: relative;
`;

const LeftLocationInputSection = styled.div`
  display: flex;
  flex-direction: column;
  width: clamp(140px, 15vw, 200px);
`;

const LocationSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: clamp(8px, 2vw, 20px);
  justify-content: space-between;
  min-width: clamp(800px, 80vw, 1250px);
  width: 100%;
`;

const LocationInputSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: clamp(12px, 2vw, 20px);
  height: 100%;
  overflow: hidden;
  width: 50%;

  &:first-child > * > :first-child {
    margin-top: clamp(3px, 1vw, 5px);
  }
`;

const LocationMapSection = styled.div`
  height: clamp(280px, 40vw, 400px);
  margin-top: clamp(3px, 1vw, 5px);
  width: clamp(360px, 60vw, 600px);
`;

const MapCanvas = styled.div`
  gap: clamp(8px, 2vw, 20px);
  height: 100%;
  position: relative;

  & .notification-configurations-map > div {
    border-radius: 8px;
  }
`;

const PolyRectPreview = styled.div`
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  color: black;
  max-height: 400px;
  min-height: 56px;
  overflow-y: auto;
  padding: 10px;
  width: 100%;
`;

const RightLocationInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1vw, 16px);
  width: 100%;
`;

const SectionContent = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 2vw, 8px);
  justify-content: center;
  min-width: clamp(800px, 80vw, 1600px);
  // padding: 0 clamp(40px, 8vw, 170px);
  position: relative;
`;

const SectionDescription = styled.div`
  color: black;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: clamp(12px, 1.5vw, 16px);
  font-weight: 400;
  line-height: clamp(18px, 2.5vw, 24px);
  position: relative;
  text-align: center;
`;

const SectionHeader = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vw, 8px);
  justify-content: flex-start;
  position: relative;
`;

const SectionHeaderContainer = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  gap: clamp(20px, 5vw, 60px);
  justify-content: center;
  min-width: clamp(800px, 80vw, 1600px);
  padding: clamp(30px, 5vw, 60px) 0 0 0;
  position: relative;
`;

const SectionTitle = styled.div`
  color: black;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: clamp(20px, 4vw, 40px);
  font-weight: 700;
  line-height: clamp(28px, 5vw, 48px);
  position: relative;
  text-align: center;
`;

const SortAndFilterSection = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  gap: clamp(12px, 3vw, 32px);
`;

const SortIcon = styled.img`
  height: auto;
  margin-left: clamp(4px, 1vw, 10px);
  text-align: center;
  width: clamp(8px, 1.5vw, 12px);
`;

const TableControlsContainer = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: clamp(800px, 80vw, 1600px);
  // padding: 0 clamp(40px, 8vw, 170px);
  width: 100%;
`;

const ToastsContainer = styled.div`
  background: rgba(255, 255, 255, 0.9);
  position: fixed;
  top: clamp(10px, 2vw, 20px);
  z-index: 10000;
`;

// Menu options for sort select menu
const sortMenuOptions = [
  {
    direction: "desc",
    label: "Notification Name (A-Z)",
    type: "Notification Name",
  },
  {
    direction: "asc",
    label: "Notification Name (Z-A)",
    type: "Notification Name",
  },
  {
    direction: "desc",
    label: "Location (A-Z)",
    type: "Location",
  },
  {
    direction: "asc",
    label: "Location (Z-A)",
    type: "Location",
  },
];

// Loader for router
export const loader = async () => {
  if (!isLoggedIn()) {
    return redirect("/login");
  } else {
    const notificationConfigurations = await getNotificationConfigurations();
    const locationTypes = await getNotificationConfigurationLocationTypes();
    const usStates = await getUsaStates();
    const notificationTypes = await getNotificationTypes();
    const faultTypes = await getFaultTypes();
    const distanceUnitTypes = await getDistanceUnitTypes();
    const contacts = await getContacts();

    return {
      notificationConfigurations,
      locationTypes,
      usStates,
      notificationTypes,
      faultTypes,
      distanceUnitTypes,
      contacts,
    };
  }
};

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}

interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

interface PlaceType {
  description: string;
  place_id: string;
  structured_formatting: StructuredFormatting;
}

const ManageNotificationConfigurations = () => {
  const { user } = useUserContext();
  const loaderData = useLoaderData() as any;
  const appLocation = useLocation();
  const navigate = useNavigate();

  const [notificationConfigurations, setNotificationConfigurations] = useState<
    any[]
  >(loaderData.notificationConfigurations);
  const [locationTypes] = useState<any[]>(loaderData.locationTypes);
  const [usStates] = useState<any[]>(
    loaderData.usStates.map((state) => state.name)
  );
  const [notificationTypes] = useState<any[]>(loaderData.notificationTypes);
  const [faultTypes] = useState<any[]>(
    [{ id: 0, codeName: "Select All" }].concat(loaderData.faultTypes)
  );
  const [distanceUnitTypes] = useState<any[]>(loaderData.distanceUnitTypes);
  const [contacts, setContacts] = useState<any[]>(loaderData.contacts);

  // Toast states
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<AlertColor>("success");

  // State to track which alert is being managed and editability
  const [expandedConfig, setExpandedConfig] = useState<string | null>(null);

  // Configurations table settings
  const [tablePage, setTablePage] = useState<number>(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState<number>(10);
  const [tableSortType, setTableSortType] =
    useState<string>("Notification Name");
  const [tableSortDirection, setTableSortDirection] = useState<string>("desc");

  // State for sort select menu displayed text
  const [sortBy, setSortBy] = useState<string>("Notification Name (A-Z)");

  const [filters, setFilters] = useState({
    faultTypes: faultTypes.map((type) => type.codeName),
    locationTypes: locationTypes.map((type) => type.codeName),
  });

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState<boolean>(false);
  const [configToDelete, setConfigToDelete] = useState<object | null>(null);

  const clearToast = useCallback(() => {
    const clear = () => {
      setToastMessage(null);
    };

    setTimeout(clear, 5000);
  }, [setToastMessage]);

  // Handler to toggle alert management dropdown
  const handleToggle = (alertId: string, e) => {
    e?.preventDefault();

    setExpandedConfig((prev) => (prev === alertId ? null : alertId));
  };

  const toggleDeleteModal = (newValue) => {
    setIsConfirmDeleteModalOpen(newValue);
    if (!newValue) setConfigToDelete(null);
  };

  const userIsAdmin = useMemo(() => {
    let isAdmin: boolean = false;
    let validRoles: string[] = ["Administrator", "SysAdmin"];
    let userRoles: string[] = user?.roles.map((role) => role.type.codeName);

    for (let i = 0; i < validRoles.length; i++) {
      isAdmin = isAdmin || userRoles.includes(validRoles[i]);
      if (isAdmin) break;
    }

    return isAdmin;
  }, [user]);

  const handleSortSelect = (e) => {
    e?.preventDefault();

    setSortBy(e.target.value);

    let sortBy = sortMenuOptions.find(
      (option) => option.label === e.target.value
    );

    setTableSortType(sortBy ? sortBy.type : "Notification Name");
    setTableSortDirection(sortBy ? sortBy.direction : "desc");
  };

  const sortListings = (listings) => {
    let tableSort = tableSortType + " " + tableSortDirection;

    switch (tableSort) {
      case "Location asc": {
        listings.sort((a, b) => {
          let aLocation =
            a.locationType.id === 4
              ? a.location.address
              : a.locationType.id === 3
              ? loaderData.usStates.find((s) => s.idAbv === a.location.state)
                  .name
              : a.locationType.id === 2
              ? "Region"
              : "Network";

          let bLocation =
            b.locationType.id === 4
              ? b.location.address
              : b.locationType.id === 3
              ? loaderData.usStates.find((s) => s.idAbv === b.location.state)
                  .name
              : b.locationType.id === 2
              ? "Region"
              : "Network";

          return aLocation < bLocation ? 1 : bLocation < aLocation ? -1 : 0;
        });
        break;
      }

      case "Location desc": {
        listings.sort((a, b) => {
          let aLocation =
            a.locationType.id === 4
              ? a.location.address
              : a.locationType.id === 3
              ? loaderData.usStates.find((s) => s.idAbv === a.location.state)
                  .name
              : a.locationType.id === 2
              ? "Region"
              : "Network";

          let bLocation =
            b.locationType.id === 4
              ? b.location.address
              : b.locationType.id === 3
              ? loaderData.usStates.find((s) => s.idAbv === b.location.state)
                  .name
              : b.locationType.id === 2
              ? "Region"
              : "Network";

          return aLocation > bLocation ? 1 : bLocation > aLocation ? -1 : 0;
        });
        break;
      }

      case "Notification Name asc": {
        listings.sort((a, b) => {
          return a.name < b.name ? 1 : b.name < a.name ? -1 : 0;
        });
        break;
      }

      case "Notification Name desc": {
        listings.sort((a, b) => {
          return a.name > b.name ? 1 : b.name > a.name ? -1 : 0;
        });
        break;
      }

      default: {
        break;
      }
    }

    return listings;
  };

  // helper function passed into the Filter Controls component
  const handleFilterChange = (filters) => {
    setFilters(filters);
  };

  // Memoized filtered notification configurations
  const filteredConfigurations = useMemo(() => {
    if (!notificationConfigurations) return null;

    let filteredConfigurations = deepCopy(notificationConfigurations);

    // Filter the notification configurations based on selected filters
    filteredConfigurations = filteredConfigurations.filter((config) => {
      let faultTypeMatch = false,
        locTypeMatch = false;

      let configFaultTypes = [];
      config.alertTypes.forEach((t) => {
        configFaultTypes.push(
          loaderData.faultTypes.find((f) => f.id === t.type).codeName
        );
      });

      for (let i = 0; i < configFaultTypes.length; i++) {
        faultTypeMatch =
          faultTypeMatch || filters.faultTypes.includes(configFaultTypes[i]);
        if (faultTypeMatch) break;
      }

      locTypeMatch =
        locTypeMatch ||
        filters.locationTypes.includes(config.locationType.codeName);

      return faultTypeMatch && locTypeMatch;
    });

    return filteredConfigurations;
  }, [notificationConfigurations, filters]);

  // Memoized notifications configurations that uses the filtered configurations,
  // sorts them, then displays only those on the current page
  const visibleConfigurations = useMemo(
    () =>
      sortListings(filteredConfigurations).slice(
        tablePage * tableRowsPerPage,
        tablePage * tableRowsPerPage + tableRowsPerPage
      ),
    [
      filteredConfigurations,
      tablePage,
      tableRowsPerPage,
      tableSortDirection,
      tableSortType,
    ]
  );

  const ConfigRow = ({ data, i }) => {
    const [tablePage, setTablePage] = useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

    const [configIsEditable, setConfigIsEditable] = useState<boolean>(false);

    // Form input states
    const [name, setName] = useState<string>(data.name || "");
    const [nameErr, setNameErr] = useState<string | null>(null);

    const [triggerType, setTriggerType] = useState<any[]>(
      data.alertTypes.map(
        (t) => loaderData.faultTypes.find((f) => f.id === t.type).codeName
      ) || []
    );
    const [triggerTypeErr, setTriggerTypeErr] = useState<string | null>(null);

    const [distance, setDistance] = useState<string>(
      data.location.distance || ""
    );
    const [distanceErr, setDistanceErr] = useState<string | null>(null);

    const [distanceUnit, setDistanceUnit] = useState(
      data.location.distanceUnit.id || distanceUnitTypes[0].id
    );
    const [distanceUnitErr, setDistanceUnitErr] = useState<string | null>(null);

    const [notifOpts, setNotifOpts] = useState<any[]>(
      data.notifications.map((notif) => {
        let codeName = notif.type.codeName;
        return {
          id: notif.id,
          user: notif.contact.id,
          type: notif.type.id,
          codeName,
          value:
            codeName === "Email"
              ? notif.contact.emailAddress
              : codeName === "SMS" || codeName === "Tel"
              ? notif.contact.cellPhone
              : "",
        };
      }) || []
    );
    const [notifOptsErr, setNotifOptsErr] = useState<string | null>(null);

    const [isNewContactModalOpen, setIsNewContactModalOpen] = useState(false);

    const [locationType, setLocationType] = useState<string | number>(
      data.locationType.id || 4
    );
    const [locationTypeErr, setLocationTypeErr] = useState<string | null>(null);

    const [region, setRegion] = useState<any[]>([]);
    const [regionErr, setRegionErr] = useState<string | null>(null);

    const [state, setState] = useState(
      (data.location.state &&
      loaderData.usStates.find((s) => s.idAbv === data.location.state)
        ? loaderData.usStates.find((s) => s.idAbv === data.location.state).name
        : data.location.state) || ""
    );
    const [stateErr, setStateErr] = useState<string | null>(null);

    const [location, setLocation] = useState<PlaceType | null>(
      data.location.address || null
    );
    const [locationInputValue, setLocationInputValue] = useState<string>(
      data.location.address || ""
    );
    const [locationErr, setLocationErr] = useState<string | null>(null);

    const [lat, setLat] = useState<string | number>(data.location.lat || "");
    const [lon, setLon] = useState<string | number>(data.location.lon || "");

    // States for preview center and radius when seleting Distance location type
    // Google Maps API expects radius in meters for Circle instances
    const [previewCenter, setPreviewCenter] = useState<any>({
      lat: Number(data.location.lat) || 0,
      lng: Number(data.location.lon) || 0,
    });
    const [previewRadius, setPreviewRadius] = useState<number>(
      (distanceUnitTypes.find((t) => t.id === data.location.distanceUnit.id)
        .longName === "Kilometer"
        ? data.location.distance * 1000
        : data.location.distance * 1609.34) || 0
    );

    // Polygon or Rectangle data retrieved from Google drawing manager/tool
    const [polyRectData, setPolyRectData] = useState<any | null>();

    // Contains location suggestions as user searches
    const [locationSuggestions, setLocationSuggestions] = useState<
      readonly PlaceType[]
    >([]);

    const getLocationSuggestions = useMemo(
      () =>
        debounce(
          (
            request: { input: string },
            callback: (results?: readonly PlaceType[]) => void
          ) => {
            (autocompleteService.current as any).getPlacePredictions(
              request,
              callback
            );
          },
          400
        ),
      []
    );

    // Initialize preview center & radius
    // useEffect(() => {
    //   setPreviewCenter({
    //     lat: Number(data.location.lat),
    //     lng: Number(data.location.lon),
    //   });
    //   setPreviewRadius(data.location.distance);
    // }, [data, setPreviewCenter, setPreviewRadius]);

    // Get lat/lon of selected location on change
    useEffect(() => {
      const getCoords = async (loc) => {
        await getLatLon(loc)
          .then((resp) => {
            setLat(String(resp.lat));
            setLon(String(resp.lon));
            setPreviewCenter({ lat: Number(resp.lat), lng: Number(resp.lon) });
          })
          .catch((err) => {
            console.error(err);
          });
      };

      if (!isLoggedIn()) {
        navigate("/login", {
          replace: true,
          state: { path: appLocation.pathname },
        });
      } else {
        if (!isNull(location)) {
          getCoords(location?.description || location);
        }
      }
    }, [location]);

    // Retrieve new location suggestions as user searches
    useEffect(() => {
      let active = true;

      if (!autocompleteService.current && (window as any).google) {
        autocompleteService.current = new (
          window as any
        ).google.maps.places.AutocompleteService();
      }
      if (!autocompleteService.current) {
        return undefined;
      }

      if (locationInputValue === "") {
        setLocationSuggestions(location ? [location] : []);
        return undefined;
      }

      getLocationSuggestions(
        { input: locationInputValue },
        (results?: readonly PlaceType[]) => {
          if (active) {
            let newOptions: readonly PlaceType[] = [];

            if (location) {
              newOptions = [location];
            }

            if (results) {
              newOptions = [...newOptions, ...results];
            }

            setLocationSuggestions(newOptions);
          }
        }
      );

      return () => {
        active = false;
      };
    }, [location, locationInputValue, getLocationSuggestions]);

    const getVisibleTableRows = useMemo(
      () =>
        [...notifOpts].slice(
          tablePage * tableRowsPerPage,
          tablePage * tableRowsPerPage + tableRowsPerPage
        ),
      [notifOpts, tablePage, tableRowsPerPage]
    );

    const revertFormData = () => {
      setName(data.name);
      setTriggerType(
        data.alertTypes.map(
          (t) => loaderData.faultTypes.find((f) => f.id === t.type).codeName
        )
      );
      setLocationType(data.locationType.id);
      setState(
        data.location.state
          ? loaderData.usStates.find(
              (ldState) => ldState.idAbv === data.location.state
            ).name
          : data.location.state
      );
      setLocation(data.location.address);
      setLocationInputValue(data.location.address);
      setDistance(data.location.distance);
      setDistanceUnit(data.location.distanceUnit.id);
      setLat(data.location.lat);
      setLon(data.location.lon);
      let notifications: any[] = [];
      for (let i = 0; i < data.notifications.length; i++) {
        let codeName = data.notifications[i].type.codeName;
        notifications.push({
          id: data.notifications[i].id,
          user: data.notifications[i].contact.id,
          type: data.notifications[i].type.id,
          codeName,
          value:
            codeName === "Email"
              ? data.notifications[i].contact.emailAddress
              : codeName === "SMS" || codeName === "Tel"
              ? data.notifications[i].contact.cellPhone
              : "",
        });
      }
      setNotifOpts(notifications);

      setPreviewCenter({
        lat: Number(data.location.lat),
        lng: Number(data.location.lon),
      });
      let unitType = distanceUnitTypes.find(
        (t) => t.id === data.location.distanceUnit.id
      ).longName;
      setPreviewRadius(
        unitType === "Kilometer"
          ? Number(data.location.distance) * 1000
          : Number(data.location.distance) * 1609.34
      );

      // setPolyRectData();

      setNameErr(null);
      setTriggerTypeErr(null);
      setDistanceErr(null);
      setDistanceUnitErr(null);
      setNotifOptsErr(null);
      setLocationTypeErr(null);
      setRegionErr(null);
      setStateErr(null);
      setLocationErr(null);
    };

    const validateForm = async () => {
      let nameIsValid = false,
        triggerTypeIsValid = false,
        locationIsValid = false,
        distanceIsValid = false,
        distanceUnitIsValid = false,
        recipientsAreValid = false;

      // Title validation
      if (name) {
        // if (validator.isAlphanumeric("" + name, "en-US", { ignore: " -" })) {
        if (name.length < 1) setNameErr("Configuration name is too short");
        else if (name.length > 64) setNameErr("Configuration name is too long");
        else {
          nameIsValid = true;
          setNameErr(null);
        }
        // } else setNameErr("Configuration name has invalid characters.");
      } else setNameErr("Configuration name cannot be empty");

      // Trigger Type validation
      if (!isEmpty(triggerType)) {
        let isValid = true;
        let validFaults = loaderData.faultTypes.map((f) => f.codeName);
        triggerType.forEach((trigger) => {
          isValid = isValid && validFaults.includes(trigger);
        });
        if (isValid) {
          triggerTypeIsValid = true;
          setTriggerTypeErr(null);
        } else setTriggerTypeErr("Fault type selection is invalid");
      } else setTriggerTypeErr("At least one fault type must be selected");

      // Location and lat/lon validation
      if (locationType !== 4) {
        locationIsValid = true;
        setLocationErr(null);
      } else if (locationInputValue && lat && lon) {
        // if (
        //   validator.isAlphanumeric("" + locationInputValue, "en-US", {
        //     ignore: " ,",
        //   })
        // ) {
        // if (locationInputValue.length < 3)
        //   setLocationErr("Location is too short");
        // else if (locationInputValue.length > 120)
        //   setLocationErr("Location is too long");
        // else {
        locationIsValid = true;
        setLocationErr(null);
        // }
        // } else setLocationErr("Location has invalid characters");
      } else setLocationErr("Location cannot be empty");

      // Distance validation
      if (locationType !== 4) {
        distanceIsValid = true;
        setDistanceErr(null);
      } else if (distance) {
        if (validator.isNumeric("" + distance)) {
          if (Number(distance) <= 0) {
            setDistanceErr("Distance must be greater than 0");
          } else {
            distanceIsValid = true;
            setDistanceErr(null);
          }
        } else setDistanceErr("Distance must be numeric");
      } else setDistanceErr("Distance cannot be empty");

      // Distance Unit validation
      if (locationType !== 4) {
        distanceUnitIsValid = true;
        setDistanceUnitErr(null);
      } else if (distanceUnit) {
        let validDistanceUnit = distanceUnitTypes.find(
          (unit) => unit.id === distanceUnit
        );
        if (validDistanceUnit) {
          distanceUnitIsValid = true;
          setDistanceUnitErr(null);
        } else setDistanceUnitErr("Invalid distance unit");
      } else setDistanceUnitErr("Distance unit must be selected");

      // // Email validation
      //   if (email) {
      //     if (validator.isEmail("" + email)) {
      //       emailIsValid = true;
      //       setEmailErr(null);
      //     } else setEmailErr("Email is invalid");
      //   } else {
      //     // Email is optional
      //     emailIsValid = true;
      //     setEmailErr(null);
      //   }

      // // SMS / Phone number validation
      //   if (sms) {
      //     if (validator.isMobilePhone("" + sms, "any")) {
      //       smsIsValid = true;
      //       setSmsErr(null);
      //     } else setSmsErr("Phone number is invalid");
      //   } else {
      //     // SMS is optional
      //     smsIsValid = true;
      //     setSmsErr(null);
      //   }

      // Recipients validation
      if (isEmpty(notifOpts)) {
        setNotifOptsErr(
          "At least one recipient must be selected to receive notifications"
        );
      } else {
        recipientsAreValid = true;
        setNotifOptsErr(null);
      }

      return (
        nameIsValid &&
        triggerTypeIsValid &&
        locationIsValid &&
        distanceIsValid &&
        distanceUnitIsValid &&
        recipientsAreValid
      );
    };

    const updateContacts = useCallback(async () => {
      await getContacts()
        .then((resp) => {
          setContacts(resp);
        })
        .catch((err) => {
          console.error(err);
        });
    }, [setContacts]);

    const toggleContactModal = (newValue) => {
      setIsNewContactModalOpen(newValue);
      updateContacts();
    };

    const handleLocationTypeChange = (e) => {
      e?.preventDefault();

      setDistance("");
      setDistanceUnit(distanceUnitTypes[0]?.id);
      setRegion([]);
      setState("");
      setLocation(null);
      setLocationInputValue("");
      setLat("");
      setLon("");

      setPolyRectData(null);

      setLocationType(e.target.value);
    };

    const handleDistanceChange = (val) => {
      let unitType = distanceUnitTypes.find(
        (t) => t.id === distanceUnit
      ).longName;
      let radius =
        unitType === "Kilometer" ? Number(val) * 1000 : Number(val) * 1609.34;

      setDistance(val);
      setPreviewRadius(radius);
    };

    const handleDistanceUnitChange = (val) => {
      let unitType = distanceUnitTypes.find((t) => t.id === val).longName;
      let radius =
        unitType === "Kilometer"
          ? Number(distance) * 1000
          : Number(distance) * 1609.34;

      setDistanceUnit(val);
      setPreviewRadius(radius);
    };

    const handleEditToggle = (alertId: string, e) => {
      e?.preventDefault();

      if (alertId === expandedConfig) {
        setConfigIsEditable(!configIsEditable);
      }
    };

    const handleSelectTriggerType = (e, val) => {
      e?.preventDefault();

      console.log(val);

      if (val[val.length - 1] === "Select All") {
        if (val.length - 1 !== loaderData.faultTypes.length) {
          val = loaderData.faultTypes.map((f) => f.codeName);
          setTriggerType(val);
        } else {
          setTriggerType([]);
        }
      } else setTriggerType(val);
    };

    const handleDelete = async (alertId: string, e) => {
      e?.preventDefault();

      if (!isLoggedIn()) {
        navigate("/login", {
          replace: true,
          state: { path: appLocation.pathname },
        });
      } else {
        await deleteNotificationConfiguration(alertId)
          .then(async (resp) => {
            setNotificationConfigurations(
              await getNotificationConfigurations()
            );
            setToastMessage("Notification configuration deleted successfully");
            setToastSeverity("success");
            clearToast();
          })
          .catch((err) => {
            console.log(err);
            setToastMessage("Failed to delete notification configuration");
            setToastSeverity("error");
            clearToast();
          });
      }
    };

    const handleSaveChanges = async (alertId: string, e) => {
      if (e) e.preventDefault();

      // const existingNotifications = notificationConfigurations.find(
      //   (cAlert) => cAlert.id === alertId
      // ).notifications;

      if (await validateForm()) {
        let notifications: any[] = [];

        for (let i = 0; i < notifOpts.length; i++) {
          notifications.push({
            type: notifOpts[i].type,
            contact: notifOpts[i].user,
          });
        }

        // console.log(notifOpts);

        // for (const [key, value] of Object.entries(notifOpts)) {
        //   if (!value) continue; // ignore empty notification inputs
        //   let n = { type: key, notificationValue: value };
        //   let e = existingNotifications.find((n) => {
        //     return n.type.id == key;
        //   });
        //   if (e) n["id"] = e.id;

        //   notifications.push(n);
        // }

        let data = {
          alertTypes: triggerType.map(
            (t) => loaderData.faultTypes.find((f) => f.codeName === t).id
          ),
          locationType,
          location: {
            lat: locationType === 4 ? String(lat) : "",
            lon: locationType === 4 ? String(lon) : "",
            distance: locationType === 4 ? distance : 0,
            distanceUnit: { id: distanceUnit },
            address: locationType === 4 ? locationInputValue : "",
            state:
              locationType === 3
                ? loaderData.usStates.find((ldState) => ldState.name === state)
                    .idAbv
                : "",
          },
          name: name,
          notifications,
        };

        if (!isLoggedIn()) {
          navigate("/login", {
            replace: true,
            state: { path: appLocation.pathname },
          });
        } else {
          await updateNotificationConfiguration(data, alertId)
            .then(async () => {
              setNotificationConfigurations(
                await getNotificationConfigurations()
              );
              setToastMessage(
                "Notification configuration updated successfully"
              );
              setToastSeverity("success");
              clearToast();
            })
            .catch((err) => {
              console.log(err);
              setToastMessage("Notification configuration failed to update");
              setToastSeverity("error");
              clearToast();
            });
        }
      }
    };

    return (
      <React.Fragment key={`config-row-${i}`}>
        <NewContactModal
          isNewContactModalOpen={isNewContactModalOpen}
          toggleContactModal={toggleContactModal}
        />

        <ConfirmDeleteModal
          configToDelete={configToDelete}
          handleDelete={handleDelete}
          isConfirmDeleteModalOpen={isConfirmDeleteModalOpen}
          toggleDeleteModal={toggleDeleteModal}
        />

        <TableRow
          className="config-row-header"
          id={`config-row-${data.id}`}
          onClick={(e) => handleToggle(data.id, e)}
          sx={{
            cursor: "pointer",
            "& td": { borderBottom: "none", color: "black" },
          }}
        >
          <Tooltip
            followCursor
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "white",
                  border: "1px solid var(--terbine-light-gray-2)",
                  color: "black",
                  fontSize: "16px",
                  fontWeight: "300",
                  maxWidth: "30vw",
                },
              },
            }}
            title={<div style={{ background: "white" }}>{data.name}</div>}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 200,
                minWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{data.name}</span>
            </TableCell>
          </Tooltip>

          <TableCell
            align="left"
            sx={{
              color: "white",
              maxWidth: 200,
              minWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span>{data.locationType.codeName}</span>
          </TableCell>

          <TableCell
            align="left"
            sx={{
              color: "white",
              maxWidth: 200,
              minWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span>
              {data.locationType.id === 4
                ? data.location.address
                : data.locationType.id === 3 &&
                  loaderData.usStates.find(
                    (s) => s.idAbv === data.location.state
                  )
                ? loaderData.usStates.find(
                    (s) => s.idAbv === data.location.state
                  ).name
                : data.locationType.id === 2
                ? "Region"
                : "Network"}
            </span>
          </TableCell>

          <TableCell
            align="center"
            sx={{
              color: "white",
              maxWidth: 200,
              minWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span>
              {data.locationType.id === 4
                ? "" +
                  data.location.distance +
                  " " +
                  data.location.distanceUnit.codeName
                : "n/a"}
            </span>
          </TableCell>

          <Tooltip
            followCursor
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "white",
                  border: "1px solid var(--terbine-light-gray-2)",
                  color: "black",
                  fontSize: "16px",
                  fontWeight: "300",
                  maxWidth: "30vw",
                },
              },
            }}
            title={
              <div style={{ background: "white" }}>
                {data.alertTypes
                  .map(
                    (t) =>
                      loaderData.faultTypes.find((f) => f.id === t.type)
                        .codeName
                  )
                  .sort()
                  .join(", ")}
              </div>
            }
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 400,
                minWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  display: "block",
                  overflowX: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                }}
              >
                {data.alertTypes
                  .map(
                    (t) =>
                      loaderData.faultTypes.find((f) => f.id === t.type)
                        .codeName
                  )
                  .sort()
                  .join(", ")}
              </span>
            </TableCell>
          </Tooltip>

          <TableCell sx={{ width: 50 }}>
            <IconButton size="small" sx={{ color: "white", cursor: "pointer" }}>
              <SortIcon
                src={expandedConfig === data.id ? CaretUpBlack : CaretDownBlack}
              />
            </IconButton>
          </TableCell>
        </TableRow>

        <TableRow
          className="config-row-expanded"
          sx={{
            "& > td": { borderBottom: "3px solid white" },
          }}
        >
          <TableCell
            colSpan={6}
            sx={{
              padding: "0",
            }}
          >
            <Collapse
              in={expandedConfig === data.id}
              timeout="auto"
              unmountOnExit
            >
              <Box sx={{ margin: 1, padding: "20px 0" }}>
                <div
                  style={{ display: "flex", flexDirection: "row", gap: "10px" }}
                >
                  <ControlsSection>
                    {!configIsEditable && (
                      <>
                        <EditButton
                          onClick={(e) => handleEditToggle(data.id, e)}
                          type="button"
                        >
                          <Icon src={EditIcon} />
                          Edit
                        </EditButton>

                        <DeleteButton
                          onClick={() => {
                            setConfigToDelete(data);
                            toggleDeleteModal(true);
                          }}
                          type="button"
                        >
                          <Icon src={TrashIconWhite} />
                          Delete
                        </DeleteButton>
                      </>
                    )}
                    {configIsEditable && (
                      <>
                        <EditButton
                          onClick={(e) => handleSaveChanges(data.id, e)}
                          type="button"
                        >
                          <Icon src={SaveIcon} />
                          Save
                        </EditButton>

                        <CancelButton
                          onClick={() => {
                            revertFormData();
                            setConfigIsEditable(false);
                          }}
                          type="button"
                        >
                          <Icon src={CancelIcon} />
                          Cancel
                        </CancelButton>
                      </>
                    )}
                  </ControlsSection>
                  <Form
                    id={`custom-alerts-form-${data.id}`}
                    onSubmit={(e) => handleSaveChanges(data.id, e)}
                  >
                    <InfoRow>
                      <FormField style={{ flexGrow: 1 }}>
                        <FormControl sx={{ width: "100%", minWidth: "400px" }}>
                          <Label>Configuration Name</Label>
                          <TextField
                            disabled={!configIsEditable}
                            error={nameErr ? true : false}
                            onChange={(e) => setName(e.target.value)}
                            value={name}
                            variant="outlined"
                          />
                        </FormControl>
                        <Info
                          $hasError={nameErr ? true : false}
                          style={{ visibility: nameErr ? "visible" : "hidden" }}
                        >
                          {nameErr
                            ? nameErr
                            : "Enter a name for the notification configuration"}
                        </Info>
                      </FormField>
                      <FormField style={{ flexGrow: 1, maxWidth: "600px" }}>
                        <FormControl sx={{ width: "100%", minWidth: "400px" }}>
                          <Label>Fault Type(s)</Label>
                          <Autocomplete
                            disableCloseOnSelect
                            disabled={!configIsEditable}
                            getOptionLabel={(option) => option}
                            id="trigger-types-autocomplete"
                            limitTags={1}
                            multiple
                            noOptionsText="No results found"
                            onChange={(e, newValue) => {
                              handleSelectTriggerType(e, newValue);
                            }}
                            options={faultTypes.map((t) => t.codeName)}
                            renderInput={(params) => {
                              const { InputProps, ...restParams } = params;
                              const { startAdornment, ...restInputProps } =
                                InputProps;
                              return (
                                <TextField
                                  {...restParams}
                                  placeholder="Search"
                                  slotProps={{
                                    input: {
                                      ...restInputProps,
                                      startAdornment: (
                                        <div
                                          style={{
                                            maxHeight: 38 * 4,
                                            overflowY: "auto",
                                          }}
                                        >
                                          {startAdornment}
                                        </div>
                                      ),
                                    },
                                  }}
                                />
                              );
                            }}
                            renderOption={(props, option, { selected }) => {
                              const { key, ...optionProps } = props;
                              return (
                                <MenuItem
                                  key={key}
                                  sx={{ mt: "4px" }}
                                  {...optionProps}
                                >
                                  {selected && <CheckmarkIcon fill="white" />}
                                  {option}
                                </MenuItem>
                              );
                            }}
                            value={triggerType}
                          />
                        </FormControl>
                        <Info $hasError={triggerTypeErr ? true : false}>
                          {triggerTypeErr
                            ? triggerTypeErr
                            : "Select one or more fault types"}
                        </Info>
                      </FormField>
                    </InfoRow>
                    <LocationSection>
                      <LocationInputSection>
                        <LeftLocationInputSection>
                          <FormField style={{ flexGrow: 1 }}>
                            <FormControl sx={{ width: "200px" }}>
                              <Label>Location Type</Label>
                              <Select
                                disabled={!configIsEditable}
                                error={locationTypeErr ? true : false}
                                id="location-type-select"
                                onChange={(e) => handleLocationTypeChange(e)}
                                value={locationType}
                              >
                                {locationTypes &&
                                  locationTypes.map((type, i) => (
                                    <MenuItem
                                      key={`location-type-menu-item-${i}`}
                                      value={type.id}
                                    >
                                      {type.codeName}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </FormField>
                        </LeftLocationInputSection>
                        <RightLocationInputSection>
                          {locationType == 1 && ( // location type = "Network Wide"
                            <FormField style={{ flexGrow: 1 }}>
                              <FormControl
                                // onClick={() => navigator.clipboard.writeText(String(lat))}
                                sx={{ width: "100%", minWidth: "75px" }}
                              >
                                <Label>Network</Label>
                                <TextField
                                  disabled
                                  value={user?.orgName}
                                />
                              </FormControl>
                            </FormField>
                          )}
                          {locationType == 2 && ( // location type = "Region"
                            <FormField style={{ flexGrow: 1 }}>
                              <FormControl
                                // onClick={() => navigator.clipboard.writeText(String(lat))}
                                sx={{ width: "100%", minWidth: "75px" }}
                              >
                                <Label>Region</Label>
                                <TextField
                                  disabled
                                  value={usStates
                                    .map((state) => state)
                                    .join(", ")}
                                />
                              </FormControl>
                            </FormField>
                          )}
                          {locationType == 3 && ( // location type = "State"
                            <FormField style={{ flexGrow: 1 }}>
                              <FormControl sx={{ width: "100%" }}>
                                <Label>State</Label>
                                <Select
                                  disabled={!configIsEditable}
                                  error={stateErr ? true : false}
                                  labelId="state-select-label"
                                  id="state-select"
                                  onChange={(e) => setState(e.target.value)}
                                  value={state}
                                >
                                  {usStates &&
                                    usStates.map((state, i) => (
                                      <MenuItem
                                        key={`state-menu-item-${i}`}
                                        value={state}
                                      >
                                        {state}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </FormField>
                          )}
                          {locationType == 4 && ( // location type = "Distance"
                            <>
                              <FormField style={{ flexGrow: 1 }}>
                                <FormControl
                                  sx={{ width: "100%", minWidth: "300px" }}
                                >
                                  <Label>Location</Label>
                                  <Autocomplete
                                    autoComplete
                                    disabled={!configIsEditable}
                                    options={locationSuggestions}
                                    getOptionLabel={(option) =>
                                      typeof option === "string"
                                        ? option
                                        : option.description
                                    }
                                    filterOptions={(x) => x}
                                    includeInputInList
                                    filterSelectedOptions
                                    noOptionsText="No locations"
                                    renderOption={(props, option) => {
                                      const { key, ...optionProps } = props;

                                      const matches =
                                        option.structured_formatting
                                          .main_text_matched_substrings || [];

                                      const parts = parse(
                                        option.structured_formatting.main_text,
                                        matches.map((match: any) => [
                                          match.offset,
                                          match.offset + match.length,
                                        ])
                                      );

                                      return (
                                        <li key={key} {...optionProps}>
                                          <Grid
                                            container
                                            sx={{ alignItems: "center" }}
                                          >
                                            <Grid
                                              item
                                              sx={{
                                                width: "calc(100% - 44px)",
                                                wordWrap: "break-word",
                                              }}
                                            >
                                              {parts.map((part, i) => (
                                                <Box
                                                  key={
                                                    "location-suggestion-box-" +
                                                    i
                                                  }
                                                  component="span"
                                                  sx={{
                                                    fontWeight: part.highlight
                                                      ? "bold"
                                                      : "regular",
                                                  }}
                                                >
                                                  {part.text}
                                                </Box>
                                              ))}
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: "text.secondary",
                                                }}
                                              >
                                                {
                                                  option.structured_formatting
                                                    .secondary_text
                                                }
                                              </Typography>
                                            </Grid>
                                          </Grid>
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        disabled={!configIsEditable}
                                        error={locationErr ? true : false}
                                        // fullWidth
                                      />
                                    )}
                                    onChange={(
                                      e: any,
                                      newValue: PlaceType | null
                                    ) => {
                                      setLocationSuggestions(
                                        newValue
                                          ? [newValue, ...locationSuggestions]
                                          : locationSuggestions
                                      );
                                      setLocation(newValue);
                                    }}
                                    onInputChange={async (e, newInputValue) => {
                                      setLocationInputValue(newInputValue);
                                    }}
                                    value={location}
                                  />
                                </FormControl>
                                <Info
                                  $hasError={locationErr ? true : false}
                                  style={{
                                    visibility: locationErr
                                      ? "visible"
                                      : "hidden",
                                  }}
                                >
                                  {locationErr
                                    ? locationErr
                                    : "Select a location"}
                                </Info>
                              </FormField>
                              <InfoRow style={{ gap: "10px" }}>
                                <FormField style={{ flexGrow: 1 }}>
                                  <FormControl
                                    // onClick={() => navigator.clipboard.writeText(String(lat))}
                                    sx={{ width: "100%", minWidth: "75px" }}
                                  >
                                    <Label>Latitude</Label>
                                    <TextField
                                      disabled
                                      value={lat}
                                    />
                                  </FormControl>
                                  {/* <Info>
                                    {"Latitude for selected location"}
                                  </Info> */}
                                </FormField>
                                <FormField style={{ flexGrow: 1 }}>
                                  <FormControl
                                    // onClick={() => navigator.clipboard.writeText(String(lon))}
                                    sx={{ width: "100%", minWidth: "75px" }}
                                  >
                                    <Label>Longitude</Label>
                                    <TextField
                                      disabled
                                      value={lon}
                                    />
                                  </FormControl>
                                  {/* <Info>
                                    {"Longitude for selected location"}
                                  </Info> */}
                                </FormField>
                              </InfoRow>
                              <FormField style={{ flexGrow: 1 }}>
                                <FormControl
                                  sx={{ width: "100%", minWidth: "250px" }}
                                >
                                  <Label>Distance</Label>
                                  <TextField
                                    disabled={!configIsEditable}
                                    error={distanceErr ? true : false}
                                    onChange={(e) =>
                                      handleDistanceChange(e.target.value)
                                    }
                                    slotProps={{ htmlInput: { min: 1 } }}
                                    type="number"
                                    value={distance}
                                    variant="outlined"
                                  />
                                </FormControl>
                                <Info
                                  $hasError={distanceErr ? true : false}
                                  style={{
                                    visibility: distanceErr
                                      ? "visible"
                                      : "hidden",
                                  }}
                                >
                                  {distanceErr
                                    ? distanceErr
                                    : "Enter a distance"}
                                </Info>
                              </FormField>
                              <FormField style={{ flexGrow: 1 }}>
                                <FormControl
                                  sx={{ width: "100%", minWidth: "250px" }}
                                >
                                  <Label>Distance Unit</Label>
                                  <Select
                                    disabled={!configIsEditable}
                                    error={distanceUnitErr ? true : false}
                                    labelId="distance-unit-select"
                                    onChange={(e) =>
                                      handleDistanceUnitChange(e.target.value)
                                    }
                                    value={distanceUnit}
                                  >
                                    {distanceUnitTypes &&
                                      distanceUnitTypes.map((type, i) => (
                                        <MenuItem
                                          key={
                                            "distance-unit-input-menu-item-" + i
                                          }
                                          value={type.id}
                                        >
                                          {type.longName + "s"}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                                <Info
                                  $hasError={distanceUnitErr ? true : false}
                                  style={{
                                    visibility: distanceUnitErr
                                      ? "visible"
                                      : "hidden",
                                  }}
                                >
                                  {distanceUnitErr
                                    ? distanceUnitErr
                                    : "Select a distance unit"}
                                </Info>
                              </FormField>
                            </>
                          )}
                          {locationType == 5 && ( // location type = "Polygon/Rectangle"
                            <FormField style={{ flexGrow: 1, gap: "0" }}>
                              <Label>Polygon/Rectangle Preview</Label>
                              <PolyRectPreview>
                                {polyRectData && polyRectData.type === "rectangle" ? (
                                  <>
                                    <p>Bounds:</p>
                                    <p>North: {polyRectData.data.bounds.north}</p>
                                    <p>East: {polyRectData.data.bounds.east}</p>
                                    <p>South: {polyRectData.data.bounds.south}</p>
                                    <p>West: {polyRectData.data.bounds.west}</p>
                                  </>
                                ) : polyRectData && polyRectData.type === "polygon" ? (
                                  <>
                                    <p>Path:</p>
                                    {polyRectData.data.path.map((latLng, i) => (
                                      <p>{i}: Lat: {latLng.lat()}, Lng: {latLng.lng()}</p>
                                    ))}
                                  </>
                                ) : null}
                              </PolyRectPreview>
                            </FormField>
                          )}
                        </RightLocationInputSection>
                      </LocationInputSection>
                      <LocationMapSection>
                        <Label>Map Preview</Label>
                        <MapCanvas>
                          <APIProvider
                            apiKey={"AIzaSyBy0cowEjTB0suohBM4h4UdiSz-ng-YPWg"}
                          >
                            <Map
                              currLocationType={Number(locationType)}
                              inEditMode={configIsEditable}
                              previewCenter={previewCenter}
                              previewRadius={previewRadius}
                              selectedLocation={
                                Number(locationType) === 4
                                  ? {
                                      type: "distance",
                                      lat,
                                      lon,
                                      distance,
                                      distanceUnit,
                                    }
                                  : Number(locationType) === 3
                                  ? { type: "state", state }
                                  : Number(locationType) === 2
                                  ? { type: "region", region }
                                  : { type: "network" }
                              }
                              setPolyRectData={setPolyRectData}
                              usStates={loaderData.usStates}
                            />
                          </APIProvider>
                        </MapCanvas>
                      </LocationMapSection>
                    </LocationSection>
                    {notifOptsErr && (
                      <InfoRow>
                        <Info $hasError={true}>{notifOptsErr}</Info>
                      </InfoRow>
                    )}
                    <InfoRow
                      id="notification-options-table-container"
                      style={{ justifyContent: "center" }}
                    >
                      <Paper
                        sx={{
                          backgroundColor: "#f2f2f2",
                          border: "1px solid #d9d9d9",
                          borderRadius: "8px",
                          boxShadow: "none",
                          mb: 2,
                          ml: "5px",
                          mr: "5px",
                          position: "relative",
                          width: "70%",
                        }}
                      >
                        <AddNewRecipientButton
                          disabled={!configIsEditable}
                          onClick={() => {
                            let notifs = [...notifOpts];
                            let notif = {
                              user: null,
                              type: notificationTypes[0].id,
                              codeName: notificationTypes[0].codeName,
                              value: "",
                            };
                            notifs.push(notif);
                            setNotifOpts(notifs);
                          }}
                          type="button"
                        >
                          + Add New
                        </AddNewRecipientButton>
                        <TableContainer
                          sx={{
                            "& *": {
                              fontFamily:
                                "Inter, Oxygen, Roboto, sans-serif !important",
                            },
                          }}
                        >
                          <Table sx={{ maxHeight: 400, width: "100%" }}>
                            <TableHead>
                              <TableRow
                                sx={{
                                  "& th": {
                                    fontSize: "clamp(12px, 1.5vw, 18px)",
                                    fontWeight: "400",
                                  },
                                }}
                              >
                                <TableCell align="left" sx={{ width: 200 }}>
                                  Recipient Name
                                </TableCell>
                                <TableCell align="left" sx={{ width: 150 }}>
                                  Type
                                </TableCell>
                                <TableCell align="left">Contact Info</TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ width: 100 }}
                                ></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {isEmpty(notifOpts) && (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    No assigned notifications
                                  </TableCell>
                                </TableRow>
                              )}

                              {!isEmpty(notifOpts) &&
                                getVisibleTableRows.map((notif, i) => (
                                  <TableRow
                                    key={"notification-options-table-row-" + i}
                                  >
                                    <TableCell align="left" sx={{ width: 200 }}>
                                      <FormField>
                                        <FormControl sx={{ width: "200px" }}>
                                          <Select
                                            disabled={!configIsEditable}
                                            onChange={(e) => {
                                              if (e.target.value !== "") {
                                                let notifs = [...notifOpts];
                                                let initialValue =
                                                  contacts.find(
                                                    (contact) =>
                                                      contact.id ===
                                                      e.target.value
                                                  ).emailAddress;
                                                let newNotif = {
                                                  user: e.target.value,
                                                  type: 1,
                                                  codeName: "Email",
                                                  value: initialValue,
                                                };
                                                notifs.splice(i, 1, newNotif);
                                                setNotifOpts(notifs);
                                              } else {
                                                setIsNewContactModalOpen(true);
                                              }
                                            }}
                                            value={notifOpts[i].user}
                                          >
                                            {userIsAdmin && (
                                              <MenuItem
                                                value={""}
                                              >{`+ New Contact`}</MenuItem>
                                            )}
                                            {contacts &&
                                              contacts.map((contact, i) => (
                                                <MenuItem
                                                  key={
                                                    "contact-select-menu-item-" +
                                                    i
                                                  }
                                                  value={contact.id}
                                                >
                                                  {contact.contactName
                                                    ? contact.contactName
                                                    : contact.nickName
                                                    ? contact.nickName
                                                    : "" +
                                                      contact.firstName +
                                                      " " +
                                                      contact.lastName}
                                                </MenuItem>
                                              ))}
                                          </Select>
                                        </FormControl>
                                      </FormField>
                                    </TableCell>
                                    <TableCell align="left" sx={{ width: 150 }}>
                                      <FormField>
                                        <FormControl sx={{ minWidth: "150px" }}>
                                          <Select
                                            disabled={!configIsEditable}
                                            onChange={(e) => {
                                              let notifs = [...notifOpts];
                                              let codeName =
                                                notificationTypes.find(
                                                  (type) =>
                                                    e.target.value === type.id
                                                ).codeName;
                                              let contact = contacts.find(
                                                (contact) =>
                                                  contact.id === notifs[i].user
                                              );
                                              let newNotif = {
                                                ...notifs[i],
                                                type: e.target.value,
                                                codeName: codeName,
                                                value:
                                                  codeName === "Email"
                                                    ? contact.emailAddress
                                                    : codeName === "SMS" ||
                                                      codeName === "Tel"
                                                    ? contact.cellPhone
                                                    : codeName === "Slack"
                                                    ? "<Contact Slack Data Here>"
                                                    : "",
                                              };
                                              notifs.splice(i, 1, newNotif);
                                              setNotifOpts(notifs);
                                            }}
                                            value={notifOpts[i].type}
                                          >
                                            {notificationTypes &&
                                              notificationTypes.map(
                                                (type, i) => (
                                                  <MenuItem
                                                    key={
                                                      "notification-type-select-menu-item-" +
                                                      i
                                                    }
                                                    value={type.id}
                                                  >
                                                    {type.codeName}
                                                  </MenuItem>
                                                )
                                              )}
                                          </Select>
                                        </FormControl>
                                      </FormField>
                                    </TableCell>
                                    <TableCell align="left">
                                      <FormField>
                                        <FormControl sx={{ width: "100%" }}>
                                          <TextField
                                            disabled
                                            onChange={(e) => {
                                              let notifs = [...notifOpts];
                                              let id = notificationTypes.find(
                                                (type) =>
                                                  notif.codeName ===
                                                  type.codeName
                                              ).id;
                                              let newNotif = {
                                                type: id,
                                                codeName: notif.codeName,
                                                value: e.target.value,
                                              };
                                              notifs.splice(i, 1, newNotif);
                                              setNotifOpts(notifs);
                                            }}
                                            placeholder={
                                              notificationTypes.find(
                                                (type) => type.id === notif.type
                                              )?.placeholder
                                            }
                                            value={notifOpts[i].value}
                                          />
                                        </FormControl>
                                      </FormField>
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ width: 100 }}
                                    >
                                      <DeleteIcon
                                        onClick={() => {
                                          let notifs = [...notifOpts];
                                          notifs.splice(i, 1);
                                          setNotifOpts(notifs);
                                        }}
                                        src={TrashIconRed}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          rowsPerPageOptions={[5, 10]}
                          component="div"
                          count={notifOpts.length}
                          rowsPerPage={tableRowsPerPage}
                          page={tablePage}
                          onPageChange={(e, newPage) => {
                            setTablePage(newPage);
                            document
                              .getElementById(
                                "notification-options-table-container"
                              )
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                          onRowsPerPageChange={(e) => {
                            setTableRowsPerPage(parseInt(e.target.value, 10));
                            setTablePage(0);
                            document
                              .getElementById(
                                "notification-options-table-container"
                              )
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                        />
                      </Paper>
                    </InfoRow>
                  </Form>
                </div>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Container>
      {toastMessage && (
        <ToastsContainer>
          <Alert
            onClose={() => setToastMessage(null)}
            severity={toastSeverity}
            variant="outlined"
          >
            {toastMessage}
          </Alert>
        </ToastsContainer>
      )}

      <NavigationBar />

      <SectionHeaderContainer>
        <SectionHeader>
          <SectionTitle id="manage-alerts-header">
            Configure Notifications
          </SectionTitle>
          <SectionDescription>
            Edit or delete existing Notification Configurations
          </SectionDescription>
        </SectionHeader>
      </SectionHeaderContainer>

      <TableControlsContainer>
        <CreateNewSection>
          <CreateNewButton
            onClick={() => navigate("/notification-configurations/create")}
            type="button"
          >
            + Create New Notification
          </CreateNewButton>
        </CreateNewSection>

        <SortAndFilterSection>
          <FormField style={{ width: "240px" }}>
            <FormControl fullWidth size="small">
              <Label>Sort</Label>
              {/* <InputLabel id="sort-select-label">Sort</InputLabel> */}
              <Select
                id="sort-select"
                // label="Sort"
                // labelId="sort-select-label"
                onChange={(e) => handleSortSelect(e)}
                value={sortBy}
              >
                {sortMenuOptions.map((option, i) => (
                  <MenuItem key={`sort-menu-option-${i}`} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          <FilterControls
            faultTypes={loaderData.faultTypes}
            handleFilterChange={handleFilterChange}
            locationTypes={loaderData.locationTypes}
          />
        </SortAndFilterSection>
      </TableControlsContainer>

      <SectionContent>
        <TableContainer
          id="manage-notification-configurations-table-container"
          style={{
            borderRadius: "8px",
          }}
        >
          <Table /*size="small" sx={{ maxHeight: 400, width: "100%" }}*/>
            <TableHead>
              <TableRow
                id="manage-notification-configurations-table-header-row"
                sx={{
                  backgroundColor: "var(--terbine-blue)",
                  "& th": {
                    color: "var(--terbine-white)",
                    fontSize: "clamp(12px, 1.5vw, 18px) !important",
                    fontWeight: "300",
                  },
                }}
              >
                <TableCell align="left" sx={{ maxWidth: 200, minWidth: 150 }}>
                  <b>Notification Name</b>
                  {tableSortType === "Notification Name" && (
                    <SortIcon
                      src={
                        tableSortDirection === "desc"
                          ? CaretDownWhite
                          : CaretUpWhite
                      }
                    />
                  )}
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 200, minWidth: 150 }}>
                  <b>Location Type</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 200, minWidth: 150 }}>
                  <b>Location</b>
                  {tableSortType === "Location" && (
                    <SortIcon
                      src={
                        tableSortDirection === "desc"
                          ? CaretDownWhite
                          : CaretUpWhite
                      }
                    />
                  )}
                </TableCell>
                <TableCell align="center" sx={{ maxWidth: 200, minWidth: 150 }}>
                  <b>Radius</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 400, minWidth: 300 }}>
                  <b>Fault Type(s)</b>
                </TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>

            <TableBody
              sx={{
                "& tr.config-row-header": { backgroundColor: "#F2F2F2" },
                "& tr.config-row-expanded": {
                  backgroundColor: "#FAFAFA",
                },
                "& tr.config-row-expanded:last-child td": {
                  borderBottom: "none",
                },
              }}
            >
              {isEmpty(visibleConfigurations) && (
                <TableRow sx={{ "& *": { color: "black" } }}>
                  <TableCell>No configurations</TableCell>
                  <TableCell colSpan={4} />
                  <TableCell sx={{ width: 50 }} />
                </TableRow>
              )}

              {!isEmpty(visibleConfigurations) &&
                visibleConfigurations.map((config, i) => (
                  <ConfigRow
                    data={config}
                    i={i}
                    key={`notification-configuration-row-${i}`}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ alignSelf: "flex-end" }}>
          <TablePagination
            component="div"
            count={filteredConfigurations.length}
            onPageChange={(e, newPage) => {
              setExpandedConfig(null);
              setTablePage(newPage);
              document
                .getElementById(
                  "manage-notification-configurations-table-container"
                )
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            onRowsPerPageChange={(e) => {
              setExpandedConfig(null);
              setTableRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
              document
                .getElementById(
                  "manage-notification-configurations-table-container"
                )
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            page={tablePage}
            rowsPerPage={tableRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </div>
      </SectionContent>
    </Container>
  );
};

export default ManageNotificationConfigurations;
